import { captureError } from 'errorTracking';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:7010/api';

// Statuses the reverse proxy answers when the API itself is unreachable.
const UPSTREAM_UNAVAILABLE = new Set([502, 503, 504]);

interface ApiResponseWrapper<T> {
  data: T;
  message: string;
  success: boolean;
}

export class ApiError extends Error {
  status: number;
  serverMessage: string;
  fieldErrors?: Record<string, string>;

  constructor(status: number, message: string, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.serverMessage = message;
    this.fieldErrors = fieldErrors;
  }
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  skipUnwrap?: boolean;
}

type TokenRefresher = () => Promise<string | null>;
type UnauthorizedHandler = () => void;

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private acceptLanguage: string | null = null;
  private refreshTokenFn: TokenRefresher | null = null;
  private onUnauthorizedFn: UnauthorizedHandler | null = null;
  private isRefreshing = false;
  private refreshQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  setLanguage(language: string | null) {
    this.acceptLanguage = language;
  }

  getLanguage(): string | null {
    return this.acceptLanguage;
  }

  setTokenRefresher(fn: TokenRefresher | null) {
    this.refreshTokenFn = fn;
  }

  setOnUnauthorized(fn: UnauthorizedHandler | null) {
    this.onUnauthorizedFn = fn;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async handleTokenRefresh(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;
    try {
      const newToken = this.refreshTokenFn ? await this.refreshTokenFn() : null;
      this.refreshQueue.forEach(({ resolve }) => resolve(newToken));
      return newToken;
    } catch (error) {
      this.refreshQueue.forEach(({ reject }) => reject(error));
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshQueue = [];
    }
  }

  // Error tracking (OBS-01). TanStack Query folds a thrown ApiError into the caller's `error`
  // state, so a 5xx never reaches the browser's global handlers — it is reported here, where the
  // client sees it, and then thrown exactly as before. Below 500 nothing is reported: those are
  // business answers the pages already render. A 502/503/504 is the proxy saying the API is
  // unreachable (a deploy, a restart), not that this endpoint is broken, so it is grouped by status
  // alone (OBS-02); any other 5xx keeps the tracker's default per-stack grouping.
  private httpFailure(response: Response, message: string, endpoint: string, method: string): ApiError {
    const error = new ApiError(response.status, message);
    if (response.status >= 500) {
      const fingerprint = UPSTREAM_UNAVAILABLE.has(response.status) ? ['upstream-unavailable', String(response.status)] : undefined;
      captureError(error, { method, endpoint, status: response.status }, fingerprint);
    }
    return error;
  }

  // A fetch that rejects never produced a response — DNS, connection refused, CORS, offline.
  // Reported with the request it was for under one shared group (OBS-02), then rethrown untouched so
  // callers see what they see today. Nothing is reported while the browser says it is offline.
  private async transportFetch(endpoint: string, method: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(`${this.baseUrl}${endpoint}`, init);
    } catch (error) {
      if (navigator.onLine !== false) {
        captureError(error, { method, endpoint, network: true }, ['network-failure']);
      }
      throw error;
    }
  }

  private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, skipUnwrap = false } = config;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers
    };

    if (this.token) {
      requestHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.acceptLanguage && !requestHeaders['Accept-Language']) {
      requestHeaders['Accept-Language'] = this.acceptLanguage;
    }

    let response = await this.transportFetch(endpoint, method, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined
    });

    // Only 401 means the token is invalid/expired and worth refreshing. A 403 is
    // "authenticated but forbidden" (a valid session hitting an endpoint its role
    // can't access) — refreshing won't help, and forcing it would log out a
    // refresh-less impersonation session on the first forbidden request.
    if (response.status === 401 && !endpoint.startsWith('/auth/')) {
      if (this.refreshTokenFn && this.token) {
        const newToken = await this.handleTokenRefresh();
        if (newToken) {
          this.token = newToken;
          requestHeaders['Authorization'] = `Bearer ${newToken}`;
          response = await this.transportFetch(endpoint, method, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined
          });
        } else {
          this.onUnauthorizedFn?.();
          throw new ApiError(response.status, 'Session expired');
        }
      } else {
        this.onUnauthorizedFn?.();
        throw new ApiError(response.status, 'Unauthorized');
      }
    }

    if (!response.ok) {
      // VEK-167 — only a read is resent after a 429. A command is not guaranteed idempotent on the
      // server, so it falls through to the error path below and reaches the caller with the server's
      // own message instead of being sent a second time.
      if (response.status === 429 && method === 'GET') {
        const retryAfter = response.headers.get('Retry-After');
        const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 2000;
        await this.delay(Math.min(waitMs, 10000));
        const retryResponse = await this.transportFetch(endpoint, method, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined
        });
        if (!retryResponse.ok) {
          throw new ApiError(429, 'Too many requests. Please try again later.');
        }
        const retryJson = await retryResponse.json();
        if (skipUnwrap) return retryJson as T;
        const retryWrapper = retryJson as ApiResponseWrapper<T>;
        if (typeof retryWrapper.success === 'boolean') {
          if (!retryWrapper.success) throw new ApiError(retryResponse.status, retryWrapper.message);
          return retryWrapper.data;
        }
        return retryJson as T;
      }

      const errorBody = await response.json().catch(() => ({ message: 'Request failed' }));

      // Two envelope shapes are in play: the legacy `errors` array/map (pre-existing repo
      // convention) and the work-order-management-v2 ApiDTO<Map<String,String>> envelope,
      // where the field → plain-language message map is the top-level `data` (see
      // ValidationErrorEnvelope in contracts/openapi.yaml). Both surface as
      // `ApiError.fieldErrors` so callers render one message per line regardless of shape.
      //
      // Three statuses carry them. Bean validation and work-order validation moved 422 → 400;
      // 422 stays accepted so a client build deployed against an older API keeps showing field
      // messages rather than a bare "Validation failed". 409 is the duplicate-customer conflict,
      // which names `email` or `phone` the same way so the form can mark the offending input.
      if (
        (response.status === 400 || response.status === 409 || response.status === 422) &&
        (errorBody.errors || (errorBody.data && typeof errorBody.data === 'object'))
      ) {
        const fieldErrors: Record<string, string> = {};
        if (Array.isArray(errorBody.errors)) {
          for (const err of errorBody.errors) {
            if (err.field && err.message) {
              fieldErrors[err.field] = err.message;
            }
          }
        } else if (errorBody.errors && typeof errorBody.errors === 'object') {
          Object.assign(fieldErrors, errorBody.errors);
        } else if (errorBody.data && typeof errorBody.data === 'object' && !Array.isArray(errorBody.data)) {
          Object.assign(fieldErrors, errorBody.data);
        }
        throw new ApiError(response.status, errorBody.message || 'Validation failed', fieldErrors);
      }

      throw this.httpFailure(response, errorBody.message || `HTTP error ${response.status}`, endpoint, method);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const json = await response.json();

    if (skipUnwrap) {
      return json as T;
    }

    const wrapper = json as ApiResponseWrapper<T>;
    if (typeof wrapper.success === 'boolean') {
      if (!wrapper.success) {
        throw new ApiError(response.status, wrapper.message || 'Request failed');
      }
      return wrapper.data;
    }

    return json as T;
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    if (this.acceptLanguage) {
      headers['Accept-Language'] = this.acceptLanguage;
    }

    let response = await this.transportFetch(endpoint, 'POST', {
      method: 'POST',
      headers,
      body: formData
    });

    // See request(): only 401 should trigger a refresh; 403 is a permission error.
    if (response.status === 401 && !endpoint.startsWith('/auth/')) {
      if (this.refreshTokenFn && this.token) {
        const newToken = await this.handleTokenRefresh();
        if (newToken) {
          this.token = newToken;
          headers['Authorization'] = `Bearer ${newToken}`;
          response = await this.transportFetch(endpoint, 'POST', {
            method: 'POST',
            headers,
            body: formData
          });
        } else {
          this.onUnauthorizedFn?.();
          throw new ApiError(response.status, 'Session expired');
        }
      } else {
        this.onUnauthorizedFn?.();
        throw new ApiError(response.status, 'Unauthorized');
      }
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw this.httpFailure(response, errorBody.message || `HTTP error ${response.status}`, endpoint, 'POST');
    }

    const json = await response.json();
    const wrapper = json as ApiResponseWrapper<T>;
    if (typeof wrapper.success === 'boolean') {
      if (!wrapper.success) {
        throw new ApiError(response.status, wrapper.message || 'Upload failed');
      }
      return wrapper.data;
    }
    return json as T;
  }

  get<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async getPublic<T>(endpoint: string): Promise<T> {
    const response = await this.transportFetch(endpoint, 'GET', {
      method: 'GET',
      headers: this.acceptLanguage ? { 'Accept-Language': this.acceptLanguage } : undefined
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
      throw this.httpFailure(response, errorBody.message || `HTTP error ${response.status}`, endpoint, 'GET');
    }
    const json = await response.json();
    const wrapper = json as ApiResponseWrapper<T>;
    if (typeof wrapper.success === 'boolean') {
      if (!wrapper.success) throw new ApiError(response.status, wrapper.message || 'Request failed');
      return wrapper.data;
    }
    return json as T;
  }

  // CUST-03 / CUST-04 — the unauthenticated writes beside getPublic: no Authorization header, never
  // enter the refresh queue. Used only by the customer profile-update page, where the code in the URL
  // is the only credential.
  patchPublic<T>(endpoint: string, body: unknown): Promise<T> {
    return this.writePublic<T>('PATCH', endpoint, body);
  }

  postPublic<T>(endpoint: string, body: unknown): Promise<T> {
    return this.writePublic<T>('POST', endpoint, body);
  }

  private async writePublic<T>(method: 'PATCH' | 'POST', endpoint: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.acceptLanguage) headers['Accept-Language'] = this.acceptLanguage;
    const response = await this.transportFetch(endpoint, method, { method, headers, body: JSON.stringify(body) });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
      // UI-21 — a 400 carries the field → message map as `data` (the ApiDTO validation envelope); surface it as
      // `fieldErrors` the way `request` does, so a public form can mark the offending input.
      if (response.status === 400 && errorBody.data && typeof errorBody.data === 'object' && !Array.isArray(errorBody.data)) {
        throw new ApiError(response.status, errorBody.message || 'Validation failed', { ...errorBody.data });
      }
      throw this.httpFailure(response, errorBody.message || `HTTP error ${response.status}`, endpoint, method);
    }
    const json = await response.json();
    const wrapper = json as ApiResponseWrapper<T>;
    if (typeof wrapper.success === 'boolean') {
      if (!wrapper.success) throw new ApiError(response.status, wrapper.message || 'Request failed');
      return wrapper.data;
    }
    return json as T;
  }

  publicBaseUrl(): string {
    return this.baseUrl;
  }

  post<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  put<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  patch<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
